import React, { useRef, useState } from "react";
import SignatureCanvas from "react-signature-canvas";
import { Oval } from "react-loader-spinner";
import { useLocation } from 'react-router-dom';
import "./PublicServiceApproval.css";
import configs from "../../../configs";
import { SvgElement, icontypesEnum } from "../../../utils/svgElement";
import { BreakdownApis } from "../../../apis/breakdownApis";
import { AxiosResponse, AxiosError } from "axios";
import { convertUTCDateToLocalDate } from '../../../utils/functions';
import { SharedProps } from "../../home/Home";
import { Account } from "../../../interfaces/models";

/* ─────────────────────────────────────────
   Icons
───────────────────────────────────────── */
const ChevronDownIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
        <path d="M3 5l4 4 4-4" stroke="#3F83F8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const CloseIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M5 5l10 10M15 5L5 15" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
);

const TrashIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        <rect x="2" y="2" width="16" height="16" rx="2" fill="#FEE2E2" />
        <path d="M5 5h10M8 5V3.5h4V5M6.5 5l.4 9h6.2L13.5 5" stroke="#EF4444" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export interface SettingsProps extends SharedProps { }



interface SigninProps extends SharedProps {
    setAccountMode: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
    onSuccessfullRegister: () => void;
    auth: Account & { cpassword: string };
    errors: Account & { cpassword: string };
    loading: boolean;
}

const PublicServiceApproval: React.FunctionComponent<SigninProps> = React.memo(
    (props: SigninProps): React.ReactElement<void> => {

        const sigPad = useRef<SignatureCanvas>(null);
        const location = useLocation();

        /* ── Parse query params from URL ── */
        const queryParams = new URLSearchParams(location.search);
        const scheduleId = queryParams.get('scheduleId') || '';
        const expiry = queryParams.get('expiry') || '';
        const token = queryParams.get('token') || '';

        /* ── Data state ── */
        const [scheduleData, setScheduleData] = React.useState<any>(null);
        const [pageLoader, setPageLoader] = React.useState<boolean>(true);
        const [pageError, setPageError] = React.useState<string>('');
        const [isExpired, setIsExpired] = React.useState<boolean>(false);
        const [alreadyApproved, setAlreadyApproved] = React.useState<boolean>(false);

        /* ── UI state ── */
        const [assetListOpen, setAssetListOpen] = useState<boolean>(true);
        const [modalOpen, setModalOpen] = useState<boolean>(false);

        /* ── Modal state ── */
        const [decisionType, setDecisionType] = useState<"approve" | "clarify">("approve");
        const [approveStep, setApproveStep] = useState<1 | 2>(1);
        const [approverName, setApproverName] = useState<string>("");
        const [position, setPosition] = useState<string>("");
        const [remark, setRemark] = useState<string>("");
        const [clarifyName, setClarifyName] = useState<string>("");
        const [clarifyQuestion, setClarifyQuestion] = useState<string>("");
        const [submitLoader, setSubmitLoader] = useState<boolean>(false);
        const [submitSuccess, setSubmitSuccess] = useState<boolean>(false);

        const [checklistModalOpen, setChecklistModalOpen] = useState<boolean>(false);
        const [selectedAssetChecklist, setSelectedAssetChecklist] = useState<any>(null);

        /* ── Fetch schedule data on mount ── */
        React.useEffect(() => {
            if (!scheduleId || !expiry || !token) {
                setPageError('Invalid or missing link parameters.');
                setPageLoader(false);
                return;
            }

            // ← remove the expiry check block entirely, let the API decide

            BreakdownApis.getPublicSchedule(scheduleId, expiry, token)
                .then((response: AxiosResponse<any>) => {
                    if (response?.data) {
                        setScheduleData(response?.data);
                        if (response.data?.approveBy) {
                            setAlreadyApproved(true);
                        }
                    }
                })
                .catch((err: AxiosError) => {
                    const status = (err.response as any)?.status;
                    const responseData = (err.response as any)?.data;
                    const errorMessage = (responseData?.ErrorMessage || '').toLowerCase();

                    if (errorMessage.includes('already used')) {
                        // Link was already used for approval — show already approved screen
                        setAlreadyApproved(true);
                    } else if (status === 400 || status === 403) {
                        setPageError('This approval link is invalid or has expired.');
                    } else {
                        setPageError('Failed to load service details. Please try again.');
                    }
                })
                .finally(() => setPageLoader(false));
        }, [scheduleId, expiry, token]);
        /* ── Modal helpers ── */
        const openModal = () => { setModalOpen(true); setDecisionType("approve"); setApproveStep(1); };
        const closeModal = () => {
            setModalOpen(false); setApproveStep(1); setApproverName("");
            setPosition(""); setRemark(""); setClarifyName(""); setClarifyQuestion("");
            sigPad.current?.clear();
        };
        const handleConfirmApproval = () => { if (approverName.trim() && position.trim()) setApproveStep(2); };
        const canConfirm = approverName.trim().length > 0 && position.trim().length > 0;

        /* ── Submit approval ── */
        const handleApproveService = (e: React.FormEvent) => {
            e.preventDefault();
            setSubmitLoader(true);

            if (!sigPad.current) return;

            sigPad.current.getTrimmedCanvas().toBlob((blob) => {
                const signatureFile = new File([blob], 'signature.png', { type: 'image/png' });

                const formData = new FormData();
                formData.append('ApproveBy', approverName);
                formData.append('ApprovalPosition', position);
                formData.append('Signature', signatureFile);
                formData.append('Comment', remark || '');

                BreakdownApis.approvePublicSchedule(scheduleId, expiry, token, formData)
                    .then((response: AxiosResponse<any>) => {
                        if (response?.data) {
                            setSubmitSuccess(true);
                            closeModal();
                        }
                    })
                    .catch((err: AxiosError) => {
                        console.error(err);
                    })
                    .finally(() => setSubmitLoader(false));
            }, 'image/png');
        };

        /* ── Submit clarification (no dedicated endpoint yet — extend when available) ── */
        const handleSendClarification = (e: React.FormEvent) => {
            e.preventDefault();
            setSubmitLoader(true);
            setTimeout(() => { setSubmitLoader(false); closeModal(); }, 1500);
        };

        const getStatusStyle = (status: string) => {
            switch (status) {
                case 'Completed': return { backgroundColor: '#E7F5F1', color: '#0E9F6E' };
                case 'Approved': return { backgroundColor: '#FFF6D8', color: '#D5AD1F' };
                case 'Inprogress': return { backgroundColor: '#FEF1E8', color: '#F97316' };
                case 'Pending': return { backgroundColor: '#FEF1E8', color: '#F97316' };
                default: return { backgroundColor: '#F3F4F6', color: '#6B7280' };
            }
        };

        /* ── Loading state ── */
        if (pageLoader) {
            return (
                <>
                    <nav className="psa-navbar">
                        <img src={`${process.env.PUBLIC_URL}/assets/img/logobig.png`} alt="Senshost" className="psa-navbar-logo" />
                    </nav>
                    <div className="psa-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <div style={{ textAlign: 'center' }}>
                            <Oval visible height="40" width="40" color="#3F83F8" secondaryColor="#CAD8FA" ariaLabel="loading" />
                            <p style={{ marginTop: '16px', color: '#6B7280', fontSize: '14px' }}>Loading service details...</p>
                        </div>
                    </div>
                </>
            );
        }

        /* ── Expired state ── */
        if (isExpired) {
            return (
                <>
                    <nav className="psa-navbar">
                        <img src={`${process.env.PUBLIC_URL}/assets/img/logobig.png`} alt="Senshost" className="psa-navbar-logo" />
                    </nav>
                    <div className="psa-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
                            <div style={{ width: '60px', height: '60px', backgroundColor: '#FCEAEA', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#E03130" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Approval Link Expired</h2>
                            <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6' }}>
                                This approval link has expired. Please contact the service team to generate a new approval link.
                            </p>
                        </div>
                    </div>
                </>
            );
        }

        /* ── Error state ── */
        if (pageError) {
            return (
                <>
                    <nav className="psa-navbar">
                        <img src={`${process.env.PUBLIC_URL}/assets/img/logobig.png`} alt="Senshost" className="psa-navbar-logo" />
                    </nav>
                    <div className="psa-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
                            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>Something went wrong</h2>
                            <p style={{ fontSize: '14px', color: '#6B7280' }}>{pageError}</p>
                        </div>
                    </div>
                </>
            );
        }

        /* ── Success state ── */
        if (submitSuccess || alreadyApproved) {
            return (
                <>
                    <nav className="psa-navbar">
                        <img src={`${process.env.PUBLIC_URL}/assets/img/logobig.png`} alt="Senshost" className="psa-navbar-logo" />
                    </nav>
                    <div className="psa-body" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
                        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
                            <div style={{ width: '60px', height: '60px', backgroundColor: '#ECFDF5', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                                    <path d="M20 6L9 17l-5-5" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
                                {alreadyApproved && !submitSuccess ? 'Already Approved' : 'Service Approved!'}
                            </h2>
                            <p style={{ fontSize: '14px', color: '#6B7280', lineHeight: '1.6' }}>
                                {alreadyApproved && !submitSuccess
                                    ? `This service was already approved by a supervisor.`
                                    : 'Your approval has been submitted successfully. The service team has been notified.'
                                }
                            </p>
                        </div>
                    </div>
                </>
            );
        }

        /* ── Main content ── */
        const assets = scheduleData?.assets || [];
        const contract = scheduleData?.contract || {};

        return (
            <>
                {/* ── Navbar ── */}
                <nav className="psa-navbar">
                    <img src={`${process.env.PUBLIC_URL}/assets/img/logobig.png`} alt="Senshost" className="psa-navbar-logo" />
                    <span className="psa-navbar-badge">Pending Approval</span>
                </nav>

                {/* ── Body ── */}
                <div className="psa-body">
                    <div className="psa-content">
                        <h1 className="psa-title">Request Service Approval</h1>
                        <p className="psa-subtitle">Review the service details below before submitting your decision.</p>

                        {/* ── Service Overview ── */}
                        <div className="psa-card">
                            <h2 className="psa-card-title">Service Overview</h2>
                            <div className="psa-overview-grid">
                                <div><p className="psa-field-label">Service Name</p><p className="psa-field-value">{contract?.name || '—'}</p></div>
                                <div><p className="psa-field-label">Contractor Name</p><p className="psa-field-value">{contract?.contractor || '—'}</p></div>
                                <div><p className="psa-field-label">Status</p>
                                    <p className="psa-field-value">
                                        <span style={{ ...getStatusStyle(scheduleData?.status), borderRadius: '10px', padding: '2px 10px', fontSize: '13px', fontWeight: '500' }}>
                                            {scheduleData?.status || '—'}
                                        </span>
                                    </p>
                                </div>
                                <div><p className="psa-field-label">Contractor Type</p><p className="psa-field-value">{contract?.contratorType || '—'}</p></div>
                                <div><p className="psa-field-label">Start Date</p><p className="psa-field-value">{scheduleData?.startDate ? convertUTCDateToLocalDate(scheduleData.startDate).slice(0, -9) : '—'}</p></div>
                                <div><p className="psa-field-label">End Date</p><p className="psa-field-value">{scheduleData?.endDate ? convertUTCDateToLocalDate(scheduleData.endDate).slice(0, -9) : '—'}</p></div>
                            </div>
                        </div>

                        {/* ── Asset List ── */}
                        <div className="psa-card">
                            <div className="psa-asset-list-header">
                                <h2 className="psa-card-title" style={{ margin: 0 }}>Asset List</h2>
                                <span className={`psa-view-details-btn${assetListOpen ? " open" : ""}`} onClick={() => setAssetListOpen(!assetListOpen)}>
                                    View Details <ChevronDownIcon />
                                </span>
                            </div>

                            {assetListOpen && (
                                <>
                                    {/* Desktop table */}
                                    <div className="psa-asset-table-wrap">
                                        <table className="psa-asset-table">
                                            <thead>
                                                <tr>
                                                    <th>Asset Name</th>
                                                    <th>Serial Number</th>
                                                    <th>Asset Location</th>
                                                    <th>Asset Model</th>
                                                    <th>Status</th>
                                                    <th>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {assets.map((item: any, i: number) => (
                                                    <tr key={i}>
                                                        <td>{item?.asset?.name || '—'}</td>
                                                        <td>{item?.asset?.serialNumber || '—'}</td>
                                                        <td>{item?.asset?.location || '—'}</td>
                                                        <td>{item?.asset?.model || '—'}</td>
                                                        <td>
                                                            <span className="psa-asset-status-badge" style={getStatusStyle(item?.status)}>
                                                                {item?.status || '—'}
                                                            </span>
                                                        </td>
                                                        <td>  {/* ← add this */}
                                                            <span
                                                                style={{ cursor: 'pointer' }}
                                                                onClick={() => { setSelectedAssetChecklist(item); setChecklistModalOpen(true); }}
                                                                title="View Checklist"
                                                            >
                                                                <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                    <rect width="28" height="28" rx="4" fill="#EBEBEB" />
                                                                    <path d="M14 15.5C14.8284 15.5 15.5 14.8284 15.5 14C15.5 13.1716 14.8284 12.5 14 12.5C13.1716 12.5 12.5 13.1716 12.5 14C12.5 14.8284 13.1716 15.5 14 15.5Z" fill="#3F83F8" />
                                                                    <path d="M17.2536 11.3089C16.2289 10.6063 15.1322 10.25 13.9934 10.25C12.9694 10.25 11.9712 10.5547 11.0264 11.1519C10.0737 11.7554 9.13969 12.8516 8.374 14C8.9932 15.0313 9.84023 16.0916 10.7224 16.6995C11.7345 17.3966 12.8348 17.75 13.9934 17.75C15.1418 17.75 16.2398 17.3968 17.2582 16.7002C18.1547 16.0859 19.0078 15.027 19.624 14C19.0057 12.9821 18.1498 11.9244 17.2536 11.3089ZM14 16.25C13.555 16.25 13.12 16.118 12.75 15.8708C12.3799 15.6236 12.0916 15.2722 11.9213 14.861C11.751 14.4499 11.7064 13.9975 11.7932 13.561C11.88 13.1246 12.0943 12.7237 12.409 12.409C12.7237 12.0943 13.1246 11.88 13.561 11.7932C13.9975 11.7064 14.4499 11.751 14.861 11.9213C15.2722 12.0916 15.6236 12.3799 15.8708 12.75C16.118 13.12 16.25 13.555 16.25 14C16.2493 14.5965 16.012 15.1684 15.5902 15.5902C15.1684 16.012 14.5965 16.2493 14 16.25Z" fill="#3F83F8" />
                                                                </svg>
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* Mobile cards */}
                                    <div className="psa-asset-cards">
                                        {assets.map((item: any, i: number) => (
                                            <div className="psa-asset-card" key={i}>
                                                <div className="psa-asset-card-header">
                                                    <span className="psa-asset-card-serial">{item?.asset?.name || '—'}</span>
                                                    <span className="psa-asset-status-badge" style={getStatusStyle(item?.status)}>{item?.status}</span>
                                                </div>
                                                <div className="psa-asset-card-grid">
                                                    <div><p className="psa-field-label">Serial Number</p><p className="psa-field-value" style={{ fontSize: '13px' }}>{item?.asset?.serialNumber || '—'}</p></div>
                                                    <div><p className="psa-field-label">Asset Location</p><p className="psa-field-value" style={{ fontSize: '13px' }}>{item?.asset?.location || '—'}</p></div>
                                                    <div><p className="psa-field-label">Asset Model</p><p className="psa-field-value" style={{ fontSize: '13px' }}>{item?.asset?.model || '—'}</p></div>
                                                </div>
                                                <div style={{ borderTop: '1px solid #F3F4F6', padding: '10px 12px', display: 'flex', justifyContent: 'flex-end' }}>
                                                    <span
                                                        style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', color: '#3F83F8', fontSize: '13px', fontWeight: 500 }}
                                                        onClick={() => { setSelectedAssetChecklist(item); setChecklistModalOpen(true); }}
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                                            <path d="M7 8.75C7.96667 8.75 8.75 7.96667 8.75 7C8.75 6.03333 7.96667 5.25 7 5.25C6.03333 5.25 5.25 6.03333 5.25 7C5.25 7.96667 6.03333 8.75 7 8.75Z" fill="#3F83F8" />
                                                            <path d="M7 3.5C4.74167 3.5 2.81583 4.90417 2.04167 7C2.81583 9.09583 4.74167 10.5 7 10.5C9.25833 10.5 11.1842 9.09583 11.9583 7C11.1842 4.90417 9.25833 3.5 7 3.5Z" fill="#3F83F8" />
                                                        </svg>
                                                        View Checklist
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* ── Signatures ── */}
                        <div className="psa-card">
                            <h2 className="psa-card-title">Signatures</h2>
                            <div className="psa-attachment-grid">
                                <div>
                                    <h3 className="psa-card-subtitle">Service Approved By</h3>
                                    <div className="psa-attachment-item">
                                        {scheduleData?.approvalSignature
                                            ? <img src={`${configs?.filePath}/${scheduleData?.approvalSignature}`} style={{ height: '50px', maxWidth: '150px' }} alt="Approval signature" />
                                            : <div className="py-2" style={{ fontWeight: '500', color: 'black', fontSize: '14px' }}>No Signature Available</div>
                                        }
                                    </div>
                                </div>
                                <div>
                                    <h3 className="psa-card-subtitle">Serviced By</h3>
                                    <div className="psa-attachment-item">
                                        {scheduleData?.contractorSignature
                                            ? <img src={`${configs?.filePath}/${scheduleData?.contractorSignature}`} style={{ height: '50px', maxWidth: '150px' }} alt="Contractor signature" />
                                            : <div className="py-2" style={{ fontWeight: '500', color: 'black', fontSize: '14px' }}>No Signature Available</div>
                                        }
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* ── Approve Button ── */}
                        <button className="psa-approve-btn" onClick={openModal}>Approve Service</button>
                    </div>
                </div>

                {/* ══ DECISION MODAL ══ */}
                {modalOpen && (
                    <div className="psa-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
                        <div className="psa-modal">
                            <div className="psa-modal-header">
                                <h3 className="psa-modal-title">Your Decision</h3>
                                <button className="psa-modal-close" onClick={closeModal}><CloseIcon /></button>
                            </div>

                            {/* <div className="psa-decision-toggle">
                                <button className={`psa-toggle-btn${decisionType === "approve" ? " active" : ""}`} onClick={() => { setDecisionType("approve"); setApproveStep(1); }}>Approve</button>
                                <button className={`psa-toggle-btn${decisionType === "clarify" ? " active" : ""}`} onClick={() => { setDecisionType("clarify"); setApproveStep(1); }}>Need Clarification</button>
                            </div> */}

                            {decisionType === "approve" && (
                                <>
                                    <div className="psa-steps">
                                        <div className="psa-step-item">
                                            <div className={`psa-step-bubble${approveStep > 1 ? " done" : " active"}`}>
                                                {approveStep > 1
                                                    ? <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2.5 7L5.5 10L11.5 4" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                                                    : "1"}
                                            </div>
                                            <span className={`psa-step-label${approveStep === 1 ? " active" : " inactive"}`}>Approver ID</span>
                                        </div>
                                        <div className={`psa-step-connector${approveStep > 1 ? " done" : ""}`} />
                                        <div className="psa-step-item">
                                            <div className={`psa-step-bubble${approveStep === 2 ? " active" : " inactive"}`}>2</div>
                                            <span className={`psa-step-label${approveStep === 2 ? " active" : " inactive"}`}>Confirmation</span>
                                        </div>
                                    </div>

                                    {approveStep === 1 && (
                                        <>
                                            <div className="psa-form-group">
                                                <label className="psa-form-label">Approver Name</label>
                                                <input type="text" className="psa-form-input" placeholder="Enter Approver Name" value={approverName} onChange={(e) => setApproverName(e.target.value)} />
                                            </div>
                                            <div className="psa-form-group">
                                                <label className="psa-form-label">Position</label>
                                                <input type="text" className="psa-form-input" placeholder="Enter Position" value={position} onChange={(e) => setPosition(e.target.value)} />
                                            </div>
                                            <button className="psa-modal-primary-btn" disabled={!canConfirm} onClick={handleConfirmApproval}>Confirm Approval</button>
                                            <button className="psa-modal-text-btn" onClick={closeModal}>Cancel</button>
                                        </>
                                    )}

                                    {approveStep === 2 && (
                                        <form onSubmit={handleApproveService}>
                                            <div className="psa-form-group">
                                                <label className="psa-form-label">Remark <span style={{ color: '#9CA3AF', fontWeight: 400 }}>(Optional)</span></label>
                                                <textarea className="psa-form-textarea" rows={3} placeholder="Add any additional comments..." value={remark} onChange={(e) => setRemark(e.target.value)} />
                                            </div>
                                            <div className="psa-form-group">
                                                <div className="psa-signature-header">
                                                    <label className="psa-form-label" style={{ margin: 0 }}>Signature</label>
                                                    <button type="button" className="psa-signature-clear" onClick={() => sigPad.current?.clear()}><TrashIcon /></button>
                                                </div>
                                                <div className="psa-signature-box">
                                                    <span className="psa-signature-hint">Sign here</span>
                                                    <SignatureCanvas ref={sigPad} penColor="black" canvasProps={{ width: 392, height: 150 }} />
                                                    <div className="psa-signature-line" />
                                                </div>
                                            </div>
                                            <button type="submit" className="psa-modal-primary-btn" disabled={submitLoader} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                {submitLoader && <Oval visible height="16" width="16" color="#fff" secondaryColor="rgba(255,255,255,0.4)" ariaLabel="loading" />}
                                                Approve Service
                                            </button>
                                            <button type="button" className="psa-modal-text-btn" onClick={() => setApproveStep(1)}>Back</button>
                                        </form>
                                    )}
                                </>
                            )}

                            {decisionType === "clarify" && (
                                <form onSubmit={handleSendClarification}>
                                    <p className="psa-clarification-subtitle">Ask a question before deciding</p>
                                    <div className="psa-form-group">
                                        <label className="psa-form-label">Full Name</label>
                                        <input type="text" className="psa-form-input" placeholder="Enter Your Name" value={clarifyName} onChange={(e) => setClarifyName(e.target.value)} />
                                    </div>
                                    <div className="psa-form-group">
                                        <label className="psa-form-label">What needs clarification? <span>*</span></label>
                                        <textarea className="psa-form-textarea" rows={4} placeholder="Describe what you need the technician to clarify" value={clarifyQuestion} onChange={(e) => setClarifyQuestion(e.target.value)} required />
                                    </div>
                                    <button type="submit" className="psa-modal-primary-btn" disabled={!clarifyQuestion.trim() || submitLoader} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                        {submitLoader && <Oval visible height="16" width="16" color="#fff" secondaryColor="rgba(255,255,255,0.4)" ariaLabel="loading" />}
                                        Send Clarification Request
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                )}

                {/* ══ CHECKLIST MODAL ══ */}
                {checklistModalOpen && selectedAssetChecklist && (
                    <div
                        className="psa-modal-overlay"
                        onClick={(e) => { if (e.target === e.currentTarget) setChecklistModalOpen(false); }}
                    >
                        <div className="psa-modal" style={{ maxHeight: '80vh', overflowY: 'auto' }}>

                            {/* Header */}
                            <div className="psa-modal-header">
                                <div>
                                    <h3 className="psa-modal-title">{selectedAssetChecklist?.asset?.name} — Checklist</h3>
                                    <p style={{ fontSize: '12px', color: '#6B7280', margin: '2px 0 0' }}>
                                        {selectedAssetChecklist?.checklistItem?.length || 0} item{selectedAssetChecklist?.checklistItem?.length !== 1 ? 's' : ''}
                                    </p>
                                </div>
                                <button className="psa-modal-close" onClick={() => setChecklistModalOpen(false)}>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                                        <path d="M5 5l10 10M15 5L5 15" stroke="#6B7280" strokeWidth="1.8" strokeLinecap="round" />
                                    </svg>
                                </button>
                            </div>

                            {/* Progress bar */}
                            {selectedAssetChecklist?.checklistItem?.length > 0 && (
                                <div style={{ marginBottom: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6B7280', marginBottom: '6px' }}>
                                        <span>
                                            {selectedAssetChecklist.checklistItem.filter((c: any) => c.isDone).length} of {selectedAssetChecklist.checklistItem.length} completed
                                        </span>
                                        <span>
                                            {Math.round((selectedAssetChecklist.checklistItem.filter((c: any) => c.isDone).length / selectedAssetChecklist.checklistItem.length) * 100)}%
                                        </span>
                                    </div>
                                    <div style={{ height: '6px', backgroundColor: '#E5E7EB', borderRadius: '99px', overflow: 'hidden' }}>
                                        <div style={{
                                            height: '100%',
                                            width: `${Math.round((selectedAssetChecklist.checklistItem.filter((c: any) => c.isDone).length / selectedAssetChecklist.checklistItem.length) * 100)}%`,
                                            backgroundColor: '#3F83F8',
                                            borderRadius: '99px',
                                            transition: 'width 0.3s ease',
                                        }} />
                                    </div>
                                </div>
                            )}

                            {/* Checklist items */}
                            {selectedAssetChecklist?.checklistItem?.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    {selectedAssetChecklist.checklistItem.map((item: any, i: number) => (
                                        <div
                                            key={i}
                                            style={{
                                                border: '1px solid #E5E7EB',
                                                borderRadius: '8px',
                                                padding: '12px 14px',
                                                backgroundColor: item.isDone ? '#F0FDF4' : '#FFFFFF',
                                            }}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                <div style={{
                                                    width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, marginTop: '1px',
                                                    backgroundColor: item.isDone ? '#10B981' : '#E5E7EB',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    {item.isDone && (
                                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                            <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                                        </svg>
                                                    )}
                                                </div>

                                                <div style={{ flex: 1 }}>
                                                    <p style={{
                                                        fontSize: '14px', margin: 0,
                                                        color: item.isDone ? '#065F46' : '#111827',
                                                        textDecoration: item.isDone ? 'line-through' : 'none',
                                                        fontWeight: 500,
                                                    }}>
                                                        {item.toDo}
                                                    </p>
                                                    {item.isDone && item.checkedDate && (
                                                        <p style={{ fontSize: '11px', color: '#6B7280', margin: '3px 0 0' }}>
                                                            Completed: {convertUTCDateToLocalDate(item.checkedDate).slice(0, -9)}
                                                        </p>
                                                    )}
                                                </div>

                                                <span style={{
                                                    fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '20px', flexShrink: 0,
                                                    backgroundColor: item.isDone ? '#D1FAE5' : '#FEF3C7',
                                                    color: item.isDone ? '#065F46' : '#92400E',
                                                }}>
                                                    {item.isDone ? 'Done' : 'Pending'}
                                                </span>
                                            </div>

                                            {item.filePath && (
                                                <div style={{
                                                    marginTop: '10px', marginLeft: '30px', padding: '8px 10px',
                                                    backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '6px',
                                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                }}>
                                                    <span style={{ fontSize: '12px', color: '#374151' }}>
                                                        Checklist Document (.{item.fileType})
                                                    </span>
                                                    <a
                                                        href={`${configs?.filePath}/${item.filePath}`}
                                                        download
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        style={{ color: '#3F83F8', fontSize: '12px', textDecoration: 'none', fontWeight: 500 }}
                                                    >
                                                        Download
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9CA3AF' }}>
                                    <svg width="48" height="48" viewBox="0 0 48 48" fill="none" style={{ margin: '0 auto 12px', display: 'block' }}>
                                        <rect x="10" y="6" width="28" height="36" rx="3" fill="#E5E7EB" />
                                        <rect x="16" y="14" width="16" height="2.5" rx="1" fill="#9CA3AF" />
                                        <rect x="16" y="20" width="16" height="2.5" rx="1" fill="#9CA3AF" />
                                        <rect x="16" y="26" width="10" height="2.5" rx="1" fill="#9CA3AF" />
                                    </svg>
                                    <p style={{ fontSize: '14px', margin: 0 }}>No checklist items for this asset</p>
                                </div>
                            )}

                            <button
                                type="button"
                                onClick={() => setChecklistModalOpen(false)}
                                style={{
                                    width: '100%', marginTop: '20px', padding: '10px',
                                    border: '1px solid #E5E7EB', borderRadius: '8px',
                                    backgroundColor: 'transparent', color: '#374151',
                                    fontSize: '14px', fontWeight: 500, cursor: 'pointer',
                                }}
                            >
                                Close
                            </button>

                        </div>
                    </div>
                )}

            </>
        );
    }
);

export default PublicServiceApproval;